import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Box,
 Button,
 Center,
 Flex,
 FormControl,
 FormLabel,
 Input,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Image,
 Select,
 SkeletonText,
 Stack,
 Table,
 Tbody,
 Td,
 Text,
 Textarea,
 Th,
 Thead,
 Tr,
 Alert,
 AlertIcon,
} from '@chakra-ui/react';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import baseService from '../../services/base';
import { carregarCapaDoPilar } from '../../core/imagens/capaDoPilar';
import { fetchData } from '../../services/fetch';
import { pillarCover } from '../../assets/new-images/icons';

// EG-40: gestão do catálogo global de Pilares.
//
// A ausência desta tela é a causa do DOR: sem lugar para ver o que já existe,
// cada questionário inventava o seu pilar — 167 registros para ~53 conceitos.
//
// Além do CRUD da story, aqui mora a ação Mesclar. Excluir um duplicado não
// resolve: o soft delete não reponta as questões, e o dashboard continua
// fragmentado, agora com pilares invisíveis na gestão.

const IDIOMAS = [
 { valor: 'pt-BR', rotulo: 'Português (BR)' },
 { valor: 'es', rotulo: 'Espanhol' },
 { valor: 'en', rotulo: 'Inglês' },
];

const FORMULARIO_VAZIO = {
 name_pillar: '',
 text_pillar: '',
 img_pillar: '',
 helper_title: '',
 helper_text: '',
 language: 'pt-BR',
};

// Mesma normalização do índice único do banco: quem procura "governanca"
// precisa achar "Governança".
const normaliza = (texto) =>
 (texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .trim()
  .toLowerCase();

// EG-42: a busca da capa saiu daqui para core/imagens/capaDoPilar.
//
// O comentário que morava nesta linha explicava por que <img src> na rota /image
// leva 401 — e mesmo assim a EG-42 introduziu exatamente esse erro em outra tela
// (IdentidadeDoPilar). Comentário num arquivo não protege outro arquivo: a regra
// virou função compartilhada, com o motivo escrito lá.
const carregarCapa = carregarCapaDoPilar;

const mensagemDoErro = (erro) =>
 erro?.response?.data?.message ||
 'Não foi possível concluir a operação. Tente novamente.';

const Pillars = () => {
 const { data: pilares, isLoading, mutate } = useSWR('/pillar', fetchData);

 const [busca, setBusca] = useState('');
 const [erro, setErro] = useState('');
 const [formulario, setFormulario] = useState(null);
 const [emExclusao, setEmExclusao] = useState(null);
 const [previaDaImagem, setPreviaDaImagem] = useState(null);
 const [arrastando, setArrastando] = useState(false);
 // Sem retorno visual, o gestor clica em Salvar e a tela fica parada até o
 // modal fechar — parece que o clique não pegou, e ele clica de novo.
 const [gravando, setGravando] = useState(false);

 const lista = useMemo(() => {
  const alvo = normaliza(busca);
  return (pilares || []).filter((pilar) =>
   alvo ? normaliza(pilar.name_pillar).includes(alvo) : true
  );
 }, [pilares, busca]);




 const salvar = async () => {
  if (gravando) return;

  if (!formulario?.name_pillar?.trim()) {
   setErro('O nome do pilar é obrigatório.');
   return;
  }

  setErro('');
  setGravando(true);
  try {
   if (formulario.id_pillar) {
    await baseService.patch(`/pillar/${formulario.id_pillar}`, formulario);
   } else {
    await baseService.post('/pillar', formulario);
   }
   setFormulario(null);
   mutate();
  } catch (falha) {
   setErro(mensagemDoErro(falha));
  } finally {
   setGravando(false);
  }
 };

 const excluir = async () => {
  if (gravando) return;

  setErro('');
  setGravando(true);
  try {
   await baseService.delete(`/pillar/${emExclusao.id_pillar}`);
   setEmExclusao(null);
   mutate();
  } catch (falha) {
   setErro(mensagemDoErro(falha));
  } finally {
   setGravando(false);
  }
 };


 const campo = (chave, valor) =>
  setFormulario((atual) => ({ ...atual, [chave]: valor }));

 // O backend aceita as duas formas no mesmo campo: a chave que já está no S3
 // (edição sem trocar a capa) ou o base64 recém-escolhido.
 const lerImagem = async (arquivo) => {
  if (!arquivo) return;

  const leitor = new FileReader();
  const base64 = await new Promise((resolve) => {
   leitor.onload = () => resolve(String(leitor.result).split(',')[1]);
   leitor.readAsDataURL(arquivo);
  });

  campo('img_pillar', base64);
  setPreviaDaImagem(`data:${arquivo.type};base64,${base64}`);
 };

 const aoEscolherImagem = (evento) => lerImagem(evento.target.files?.[0]);

 return (
  <MainContent>
   <PageHeaderStructure
    title='Pilares'
    subtitle='Catálogo global de pilares. Um pilar cadastrado aqui fica disponível para todos os questionários.'
   >
    <Flex gap='12px'>
     <Button
      onClick={() => {
       setPreviaDaImagem(null);
       setFormulario({ ...FORMULARIO_VAZIO });
      }}
     >
      Novo Pilar
     </Button>
    </Flex>
   </PageHeaderStructure>

   {/* O erro pertence ao modal aberto; mostrar também aqui duplicaria a
       mensagem na tela. */}
   {erro && !formulario && !emExclusao && (
    <Alert status='error' mt='16px' borderRadius='12px'>
     <AlertIcon />
     {erro}
    </Alert>
   )}

   <Box bg='white' borderRadius='12px' p='24px' mt='24px'>
    <Input
     placeholder='Buscar por nome'
     value={busca}
     mb='16px'
     maxW='320px'
     onChange={(evento) => setBusca(evento.target.value)}
    />

    {isLoading ? (
     <SkeletonText noOfLines={4} spacing='4' />
    ) : (
     <Table size='sm'>
      <Thead>
       <Tr>
        <Th>Nome</Th>
        <Th>Idioma</Th>
        <Th>Descrição</Th>
        <Th>Temas</Th>
        <Th>Ações</Th>
       </Tr>
      </Thead>
      <Tbody>
       {lista.map((pilar) => (
        <Tr key={pilar.id_pillar}>
         <Td>{pilar.name_pillar}</Td>
         <Td>{pilar.language}</Td>
         <Td>{pilar.text_pillar}</Td>
         <Td>{pilar.qtde_temas}</Td>
         <Td>
          <Flex gap='8px'>
           <Button
            size='xs'
            variant='ghost'
            onClick={async () => {
             setPreviaDaImagem(null);
             setFormulario({ ...pilar });
             setPreviaDaImagem(await carregarCapa(pilar.img_pillar));
            }}
           >
            Editar
           </Button>
           <Button
            size='xs'
            variant='ghost'
            colorScheme='red'
            onClick={() => setEmExclusao(pilar)}
           >
            Excluir
           </Button>
          </Flex>
         </Td>
        </Tr>
       ))}
      </Tbody>
     </Table>
    )}
   </Box>

   {/* Formulário — cinco campos: o conteúdo editorial virou global e só pode
       ser mantido aqui, porque saiu das telas de criação de questionário. */}
   <Modal isOpen={!!formulario} onClose={() => setFormulario(null)}>
    <ModalOverlay />
    <ModalContent>
     <ModalHeader>
      {formulario?.id_pillar ? 'Editar pilar' : 'Novo pilar'}
     </ModalHeader>
     <ModalCloseButton />
     <ModalBody>
      {erro && (
       <Alert status='error' mb='16px' borderRadius='8px'>
        <AlertIcon />
        {erro}
       </Alert>
      )}
      <Stack spacing='12px'>
       <FormControl isRequired>
        <FormLabel htmlFor='name_pillar'>Nome</FormLabel>
        <Input
         id='name_pillar'
         value={formulario?.name_pillar || ''}
         onChange={(e) => campo('name_pillar', e.target.value)}
        />
       </FormControl>
       <FormControl>
        <FormLabel htmlFor='language'>Idioma</FormLabel>
        <Select
         id='language'
         value={formulario?.language || 'pt-BR'}
         onChange={(e) => campo('language', e.target.value)}
        >
         {IDIOMAS.map(({ valor, rotulo }) => (
          <option key={valor} value={valor}>
           {rotulo}
          </option>
         ))}
        </Select>
       </FormControl>
       <FormControl>
        <FormLabel htmlFor='text_pillar'>Descrição</FormLabel>
        <Textarea
         id='text_pillar'
         value={formulario?.text_pillar || ''}
         onChange={(e) => campo('text_pillar', e.target.value)}
        />
       </FormControl>
       <FormControl>
        <FormLabel htmlFor='img_pillar'>Imagem</FormLabel>
        {/* O AD-10 tirou o upload de capa das telas de questionário; ele vive
            aqui agora — e com a MESMA identidade visual do CoverInput que
            existia lá: área quadrada, ilustração e o input escondido atrás do
            label. Um botão de arquivo solto não comunica que ali entra capa. */}
        {/* Sem preventDefault no dragOver e no drop, o navegador assume o
            comportamento padrão e ABRE a imagem numa aba nova em vez de
            anexá-la. A área precisa dizer explicitamente que aceita o arquivo. */}
        <Center
         data-testid='area-de-capa'
         bg={arrastando ? 'rgba(0, 98, 255, 0.08)' : 'rgba(92, 96, 102, 0.1)'}
         border={arrastando ? '2px dashed #0062FF' : '2px solid transparent'}
         w='264px'
         h='176px'
         borderRadius='8px'
         overflow='hidden'
         transition='background 0.15s'
         onDragOver={(evento) => {
          evento.preventDefault();
          setArrastando(true);
         }}
         onDragLeave={() => setArrastando(false)}
         onDrop={(evento) => {
          evento.preventDefault();
          setArrastando(false);
          lerImagem(evento.dataTransfer?.files?.[0]);
         }}
        >
         <FormLabel
          htmlFor='img_pillar'
          cursor='pointer'
          w='full'
          h='full'
          display='flex'
          alignItems='center'
          justifyContent='center'
          pos='relative'
          m={0}
         >
          {previaDaImagem ? (
           <Image
            src={previaDaImagem}
            alt='Capa do pilar'
            pos='absolute'
            width='full'
           />
          ) : (
           <Center flexDir='column'>
            <Image src={pillarCover} alt='' mb='12px' />
            <Text as='span' fontSize='sm' color='#5C6066'>
             Imagem de capa
            </Text>
            <Text as='span' fontSize='xs' color='#797D9A'>
             Clique ou arraste
            </Text>
           </Center>
          )}

          <Input
           id='img_pillar'
           type='file'
           accept='.jpg,.jpeg,.png,.svg'
           display='none'
           onChange={aoEscolherImagem}
          />
         </FormLabel>
        </Center>
       </FormControl>
       <FormControl>
        <FormLabel htmlFor='helper_title'>Título do help</FormLabel>
        <Input
         id='helper_title'
         value={formulario?.helper_title || ''}
         onChange={(e) => campo('helper_title', e.target.value)}
        />
       </FormControl>
       <FormControl>
        <FormLabel htmlFor='helper_text'>Texto do help</FormLabel>
        <Textarea
         id='helper_text'
         value={formulario?.helper_text || ''}
         onChange={(e) => campo('helper_text', e.target.value)}
        />
       </FormControl>
      </Stack>
     </ModalBody>
     <ModalFooter gap='12px'>
      <Button
       variant='ghost'
       isDisabled={gravando}
       onClick={() => setFormulario(null)}
      >
       Cancelar
      </Button>
      <Button isLoading={gravando} loadingText='Salvando' onClick={salvar}>
       Salvar
      </Button>
     </ModalFooter>
    </ModalContent>
   </Modal>

   <Modal isOpen={!!emExclusao} onClose={() => setEmExclusao(null)}>
    <ModalOverlay />
    <ModalContent>
     <ModalHeader>Excluir pilar</ModalHeader>
     <ModalCloseButton />
     <ModalBody>
      {erro && (
       <Alert status='error' mb='16px' borderRadius='8px'>
        <AlertIcon />
        {erro}
       </Alert>
      )}
      <Text>
       O pilar sai da seleção mas continua visível no histórico dos
       questionários que já o usaram.
      </Text>
     </ModalBody>
     <ModalFooter gap='12px'>
      <Button variant='ghost' onClick={() => setEmExclusao(null)}>
       Cancelar
      </Button>
      <Button
       colorScheme='red'
       isLoading={gravando}
       loadingText='Excluindo'
       onClick={excluir}
      >
       Confirmar
      </Button>
     </ModalFooter>
    </ModalContent>
   </Modal>

  </MainContent>
 );
};

export default Pillars;
